import { type UIEvent, useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, ContainerActions, Icon, layoutStore, SpinnerLoader, Typography } from 'shared/ui'
import { actionWakeMode } from 'shared/lib'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useEventEmitter } from 'entities/EventEmitter'
import { DropdownFilter } from 'features/DropdownFilter'
import { DropdownOrder } from 'features/DropdownOrder'
import { ConversationCard, ConversationNewCard } from 'pages/conversations/ui/ConversationCard'
import { SelectAction } from 'pages/conversations/ui/ConversationList/ui/SelectAction/SelectAction'
import { useConversationListContext } from 'pages/conversations/ui/ConversationList/context'
import { BulkActions } from 'pages/conversations/ui/ConversationList/ui/BulkActions'
import { useEventConversationReassigned } from 'pages/conversations/ui/ConversationList/events/EventConversationReassigned'
import { useEventMessageUpdatedNew } from '../events/EventMessageUpdatedNew'
import { useEventMessageReceivedNew } from '../events/EventMessageReceivedNew'
import { useEventVoicemailReceived } from '../events/EventVoicemailReceived'
import { useEventConversationUnread } from '../events/EventConversationUnread'
import { useEventConversationClosed } from '../events/EventConversationClosed'
import { useEventConversationOpened } from '../events/EventConversationOpened'
import { useEventConversationBulkFavorite } from '../events/EventConversationBulkFavorite'
import { useEventConversationMentionUnread } from '../events/EventConversationMentionUnread'
import styles from './styles.module.scss'

export const ConversationListContent = observer(() => {
  const { conversationStore, usersStore, inboxesStore } = useEntitiesContext()
  const listRef = useRef<HTMLDivElement | null>(null)
  const { currentItemId } = conversationStore
  const store = useConversationListContext()
  const {
    updateConversations,
    searchConversations,
    handleScrollPosition,
    listOrders,
    loadingMore,
    loading,
    isEmpty,
    hasMore,
    hasSelected,
    scrollTrigger,
    scrollPosition,
    onOpenConversation,
    showConversationsCount,
  } = store
  const { searchDropdownStore } = store.conversationSearchStore
  const typeInbox = inboxesStore.localInboxes.typeInbox

  const handleScroll = (e: UIEvent) => {
    const target = e.target as HTMLDivElement
    const scrollHeight = target.scrollHeight
    const scrollTop = target.scrollTop
    const offsetHeight = target.offsetHeight

    handleScrollPosition(scrollTop)
    if (loading) return
    if (loadingMore) return
    if (!hasMore) return
    if (scrollHeight <= scrollTop + offsetHeight + 100) {
      if (store.conversationSearchStore.hasSearchParams && !searchDropdownStore.isOpen) {
        if (store.conversationSearchStore.hasMore) searchConversations()
      } else {
        updateConversations()
      }
    }
  }

  useEventConversationUnread(store, typeInbox)
  useEventMessageReceivedNew(store, typeInbox)
  useEventMessageUpdatedNew(store, typeInbox)
  useEventVoicemailReceived(store, typeInbox)
  useEventConversationMentionUnread(store, typeInbox)
  useEventConversationClosed(store, typeInbox)
  useEventConversationOpened(store, typeInbox)
  useEventConversationBulkFavorite(typeInbox)
  useEventConversationReassigned(store, typeInbox)

  useEventEmitter('updateConversationsFilter', (payload) => {
    store.filtersStore.setFilter(payload.id, true)
    store.changeQuickFilter(payload.id)
  })
  useEventEmitter('initConversationsList', () => {
    store.initReactions()
  })
  useEventEmitter('load_conversation_list', () => {
    store.load()
  })
  useEventEmitter('reset_conversation_list', () => {
    store.reset()
  })

  useEffect(() => {
    store.handleChangeFirstLoading(true)
    store.handleChangeVisibleUI(true)
    store.initReactions()

    return () => {
      store.handleChangeFirstLoading(false)
      store.handleChangeVisibleUI(false)
      store.reset()
      store.resetReactions()
    }
  }, [])

  useEffect(
    () =>
      actionWakeMode(() => {
        store.reset()
        store.load()
      }),
    []
  )

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = scrollPosition
    }
  }, [scrollTrigger])

  const isShouldRequestAccess = !!usersStore.user?.isShouldRequestAccess

  const showActions =
    !store.conversationSearchStore.hasSearchParams && !hasSelected && !isShouldRequestAccess

  return (
    <>
      {hasSelected && (
        <ContainerActions
          leftActions={[{ iconButtonComponent: <SelectAction /> }]}
          rightActions={[{ iconButtonComponent: <BulkActions /> }]}
          disabled={loading}
        />
      )}
      {store.conversationSearchStore.hasSearchParams && (
        <div className={styles.total}>
          <Typography variant={'body-md-medium'} ariaLabel={'results'}>
            {store.conversationSearchStore.total} results
          </Typography>
        </div>
      )}
      {showActions && (
        <ContainerActions
          disabled={loading}
          padding='8px 12px 0 8px'
          leftActions={[
            {
              iconButtonComponent: showConversationsCount ? (
                <Box ml={'12px'}>
                  <Typography variant={'body-md-medium'} ariaLabel={'results'}>
                    {`${conversationStore.itemsTotalCount} Conversations`}
                  </Typography>
                </Box>
              ) : (
                <DropdownFilter store={store.filtersStore} />
              ),
            },
          ]}
          rightActions={[{ iconButtonComponent: <DropdownOrder store={store.ordersStore} /> }]}
        />
      )}
      {loading && !loadingMore && (
        <div className={styles.listStatus}>
          <SpinnerLoader ariaLabel={'ConversationListContent'} />
        </div>
      )}
      {!loading && isEmpty && !store.conversationSearchStore.hasSearchParams && (
        <div className={styles.listStatus}>
          <Icon icon={'chatEmpty'} fontSize={24} />
          {inboxesStore.localInboxes.hasQuickFilter ? (
            <p>No open conversations</p>
          ) : (
            <p>No conversations</p>
          )}
        </div>
      )}
      {!loading && isEmpty && store.conversationSearchStore.hasSearchParams && (
        <div className={styles.listStatus}>
          <Icon icon={'filter'} fontSize={24} />
          <p>No matching conversations</p>
        </div>
      )}
      <div
        className={`${styles.inboxes} ${loadingMore ? styles.loadingMoreList : ''}`}
        onScroll={handleScroll}
        ref={listRef}
      >
        {(!loading || loadingMore) &&
          listOrders.map((conversation) => {
            if (conversation.isNew) {
              return (
                <ConversationNewCard
                  key={conversation.id}
                  isActive={Number(currentItemId) === conversation.id}
                />
              )
            }

            return (
              <ConversationCard
                key={conversation.id}
                conversation={conversation}
                isActive={!layoutStore.isMobileView && Number(currentItemId) === conversation.id}
                onOpenAction={onOpenConversation}
              />
            )
          })}
        {loadingMore && (
          <div className={styles.loadingMore}>
            <SpinnerLoader ariaLabel={'ConversationListContent'} size={16} />
          </div>
        )}
      </div>
    </>
  )
})
